import type { PropAsset, PropCarouselAsset, PropCarouselSlide, PropContent, ValuePropAsset, ValuePropCarouselAsset, ValuePropLike, VisibleSlide } from './propValues.types';

/**
 * True for the "carousel" value-prop shape (a list of slides) vs a single-prop tile. Centralises
 * the `slides` discriminant used across the payload → tile pipeline so a new value-prop shape has
 * one guard to update rather than several ad-hoc `'slides' in card` checks. Overloaded to narrow
 * both the authored asset (pre-layout) and the positioned asset (post-layout).
 */
export function isCarouselAsset(card: ValuePropAsset | ValuePropCarouselAsset): card is ValuePropCarouselAsset;
export function isCarouselAsset(card: PropAsset | PropCarouselAsset): card is PropCarouselAsset;
export function isCarouselAsset(card: ValuePropAsset | ValuePropCarouselAsset | PropAsset | PropCarouselAsset): boolean {
  return 'slides' in card;
}

/** True for either value-prop tile shape once it carries its `type` tag (post layout pass). */
export const isValuePropLike = (item: { type: string }): item is ValuePropLike => item.type === 'prop' || item.type === 'propCarousel';

/** The asset in `contentList` that should show at the current breakpoint, if any. */
export const assetForBreakpoint = (contentList: ReadonlyArray<PropContent>, isMobile: boolean): PropContent | undefined =>
  contentList.find(content => content.responsiveTarget === (isMobile ? 'mobile' : 'desktop'));

/**
 * Collapse each carousel slide to its current-breakpoint asset, dropping slides with none (e.g. the
 * desktop-only foil video on mobile). Single source of truth for which slides render — shared by
 * ValuePropListCard (what to show) and pagination (whether the tile is renderable at all).
 */
export const getVisibleSlides = (slides: ReadonlyArray<PropCarouselSlide>, isMobile: boolean): VisibleSlide[] =>
  slides.reduce<VisibleSlide[]>((acc, slide) => {
    const asset = assetForBreakpoint(slide.contentList, isMobile);
    if (asset) {
      acc.push({ asset, link: slide.link, telemetryId: slide.telemetryId });
    }
    return acc;
  }, []);
